import { Link } from 'react-router-dom'
import {
  Activity,
  CheckCircle2,
  DollarSign,
  Loader2,
  XCircle,
  Clapperboard,
  Users,
  Image,
  ListOrdered,
} from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { useJobs } from '@/hooks/useJobs'
import { StatusBadge } from '@/components/jobs/StatusBadge'
import { cn, formatDate, truncate } from '@/lib/utils'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

export function Dashboard() {
  const { user } = useAuth()
  const { jobs, loading } = useJobs()

  const firstName = user?.full_name ? user.full_name.split(' ')[0] : null

  const totalJobs = jobs.length
  const completedJobs = jobs.filter((j) => j.status === 'completed').length
  const inProgressJobs = jobs.filter(
    (j) => j.status === 'queued' || j.status === 'processing'
  ).length
  const failedJobs = jobs.filter((j) => j.status === 'failed').length
  const totalCost = jobs.reduce((sum, j) => sum + (j.cost_cents || 0), 0)

  const recentJobs = jobs.slice(0, 8)

  return (
    <div className="space-y-8 max-w-6xl animate-fade-in">
      {/* Welcome header */}
      <section className="space-y-1">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
          {firstName ? `Welcome back, ${firstName}.` : 'Welcome back.'}
        </h1>
        <p className="text-sm text-muted-foreground">
          Here's an overview of your generation activity.
        </p>
      </section>

      {/* Stats row */}
      <section className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard
          label="Total Jobs"
          value={loading ? null : totalJobs}
          icon={<Activity className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          label="Completed"
          value={loading ? null : completedJobs}
          valueClassName="text-success"
          icon={<CheckCircle2 className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          label="In Progress"
          value={loading ? null : inProgressJobs}
          icon={
            <Loader2
              className={cn(
                'h-4 w-4 text-muted-foreground',
                inProgressJobs > 0 && 'animate-spin'
              )}
            />
          }
        />
        <StatCard
          label="Failed"
          value={loading ? null : failedJobs}
          valueClassName="text-destructive"
          icon={<XCircle className="h-4 w-4 text-muted-foreground" />}
        />
        <StatCard
          label="Total Cost"
          value={loading ? null : totalCost}
          displayValue={loading ? null : `$${(totalCost / 100).toFixed(2)}`}
          icon={<DollarSign className="h-4 w-4 text-muted-foreground" />}
        />
      </section>

      {/* Tabs section */}
      <Tabs defaultValue="recent-jobs">
        <TabsList>
          <TabsTrigger value="recent-jobs">Recent Jobs</TabsTrigger>
          <TabsTrigger value="quick-actions">Quick Actions</TabsTrigger>
        </TabsList>

        {/* Recent Jobs tab */}
        <TabsContent value="recent-jobs" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="space-y-2 p-6">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : recentJobs.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-4">
                  <Activity className="h-8 w-8 text-muted-foreground mb-3" />
                  <p className="text-sm font-medium">No jobs yet</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Head to Create Ad to start your first generation.
                  </p>
                  <Button asChild size="sm" className="mt-4">
                    <Link to="/create">Start creating</Link>
                  </Button>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[110px]">Status</TableHead>
                        <TableHead>Prompt</TableHead>
                        <TableHead className="w-[100px]">Ratio</TableHead>
                        <TableHead className="w-[90px]">Duration</TableHead>
                        <TableHead className="w-[140px]">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {recentJobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell>
                            <StatusBadge status={job.status} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {truncate(job.prompt_text, 60)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="font-mono text-xs">
                              {job.aspect_ratio}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {job.duration_seconds}s
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDate(job.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="px-4 py-2 border-t text-xs text-muted-foreground">
                    {completedJobs} completed · {failedJobs} failed · ${(totalCost / 100).toFixed(2)} spent
                  </div>
                  <div className="border-t px-4 py-3 flex justify-end">
                    <Button asChild variant="ghost" size="sm">
                      <Link to="/created-assets">View all created assets &rarr;</Link>
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Quick Actions tab */}
        <TabsContent value="quick-actions" className="mt-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card>
              <CardHeader>
                <Clapperboard className="h-5 w-5" />
                <CardTitle className="text-lg">Create Ad</CardTitle>
                <CardDescription>Your primary creative workspace.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild className="w-full">
                  <Link to="/create">Start creating</Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Users className="h-5 w-5" />
                <CardTitle className="text-lg">Avatars</CardTitle>
                <CardDescription>Generate AI avatar images and videos.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/avatars">Open studio</Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <Image className="h-5 w-5" />
                <CardTitle className="text-lg">Saved Context</CardTitle>
                <CardDescription>Browse and manage your uploaded assets.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/saved-context">Browse context</Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <ListOrdered className="h-5 w-5" />
                <CardTitle className="text-lg">Created Assets</CardTitle>
                <CardDescription>Track and review every generated ad and video.</CardDescription>
              </CardHeader>
              <CardContent>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/created-assets">View assets</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// StatCard
// ---------------------------------------------------------------------------

interface StatCardProps {
  label: string
  value: number | null
  icon: React.ReactNode
  valueClassName?: string
  displayValue?: string | null
}

function StatCard({ label, value, icon, valueClassName, displayValue }: StatCardProps) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="shrink-0">{icon}</span>
      </CardHeader>
      <CardContent>
        {value === null ? (
          <Skeleton className="h-8 w-12" />
        ) : (
          <span className={cn('text-2xl font-bold', valueClassName)}>
            {displayValue ?? value}
          </span>
        )}
      </CardContent>
    </Card>
  )
}
